import type { ColumnDef } from '@tanstack/react-table';
import type { CreditMemoReportRow } from '../../../services/accountingReports.service';
import { formatCurrency } from '../../../utils/money';

type Col = ColumnDef<CreditMemoReportRow>;
const txt = (accessorKey: string, header: string, size = 150): Col => ({ accessorKey, header, size });

/**
 * V1 reports_cms.pl:4735-4749, column for column and in its `%cols` sort order, plus
 * three the spreadsheet does not carry:
 *
 *  * **GL Description** — V1 exports the bare GL code. The code is what Sage needs; the
 *    description is what tells a reader whether it was a Houston or a Dallas trucking
 *    credit without a second lookup, and the credit memo PDF prints it for the customer.
 *  * **Bill To Name** — V1 exports only the Sage tag. Same reasoning.
 *  * **Sage Export Date** — the difference between a credit Sage has taken and one still
 *    pending. Nothing else in 360 surfaces `rebillinfo.masexportdate`.
 */
export const creditMemoColumns: Col[] = [
  txt('pronumber', 'Pronumber', 120),
  txt('creditmemonum', 'Credit Memo #', 130),
  txt('createdby', 'Created By', 140),
  { accessorKey: 'createdatetime', header: 'Create Date', size: 150,
    meta: { isDate: true, showTime: true } },
  txt('type', 'Type', 100),
  { accessorKey: 'rebilldate', header: 'Rebill Date', size: 120,
    meta: { isDate: true, showTime: false } },
  txt('glcode', 'GL Code', 120),
  txt('gldescription', 'GL Description', 220),
  { accessorKey: 'amount', header: 'Amount', size: 130,
    cell: ({ getValue }) => formatCurrency(getValue()), meta: { aggType: 'sum' } },
  txt('rebilledinvoice', 'Rebilled Invoice #', 150),
  txt('applyto', 'Apply To', 130),
  txt('mastag', 'Sage Tag', 120),
  txt('billtoname', 'Bill To Name', 220),
  txt('deptresponsible', 'Dept Responsible', 220),
  txt('preventable', 'Preventable', 110),
  { accessorKey: 'masexportdate', header: 'Sage Export Date', size: 140,
    meta: { isDate: true, showTime: false } },
  txt('comments', 'Comments', 300),
];
